'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { buildCharacterSlug } from '@/adapter/route'
import type {
  NotificationListItem,
  NotificationTab,
  NotificationUnreadData,
} from '@/lib/api/notification'
import { navigateReplace } from '@/lib/utils/navigation'
import { buildCreatorPath } from '@/lib/utils/share'
import { useCharacterPreviewStore } from '@/store/characterPreviewStore'
import { useNotificationStore } from '@/store/notificationStore'
import {
  type UseNotificationNavigationResult,
  useNotificationNavigation,
} from './useNotificationNavigation'
import { useNotifications } from './useNotifications'

const NOTIFICATION_REFRESH_INTERVAL = 10 * 60 * 1000

interface UseNotificationInboxOptions {
  navigationMode?: 'page' | 'modal'
  onOpenDetail?: (item: NotificationListItem) => void
  onNavigate?: () => void
  observerRootMargin?: string
}

export interface UseNotificationInboxResult {
  tab: NotificationTab
  setTab: (tab: NotificationTab) => void
  unread: NotificationUnreadData
  items: NotificationListItem[]
  loading: boolean
  loadMoreRef: RefObject<HTMLDivElement | null>
  openItem: (item: NotificationListItem) => void
  openActor: (item: NotificationListItem) => void
  openNpc: (item: NotificationListItem) => void
  readAll: () => Promise<number>
  refresh: () => Promise<void>
}

function getNotificationTab(value: string | null): NotificationTab {
  if (value === 'personal' || value === '2') return 2
  if (value === 'interaction' || value === 'interactions' || value === '3') return 3
  return 1
}

function getNotificationTabParam(tab: NotificationTab): string {
  if (tab === 2) return 'personal'
  if (tab === 3) return 'interaction'
  return 'system'
}

/**
 * Coordinates a notification inbox across mobile pages and desktop popovers.
 * Callers only choose presentation; fetching, pagination, tab state, and navigation stay here.
 */
export function useNotificationInbox({
  navigationMode = 'page',
  onOpenDetail,
  onNavigate,
  observerRootMargin = '160px 0px',
}: UseNotificationInboxOptions = {}): UseNotificationInboxResult {
  const router = useRouter()
  const searchParams = useSearchParams()
  const tabParam = searchParams.get('tab')
  const [tab, setTab] = useState<NotificationTab>(() => getNotificationTab(tabParam))
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const unread = useNotificationStore((state) => state.unread)
  const { items, loading, hasMore, loadMore, readAll, refresh, poll, markItemRead } =
    useNotifications(tab)
  const navigationActions: UseNotificationNavigationResult = useNotificationNavigation({
    tab,
    mode: navigationMode,
    onOpenDetail,
    beforeNavigate: onNavigate,
  })
  const { openItem: navigateItem, openPath: navigatePath } = navigationActions

  const handleOpenItem = useCallback(
    (item: NotificationListItem) => {
      if (item.tab === 3) return
      if (!item.read) markItemRead(item.id)
      navigateItem(item)
    },
    [markItemRead, navigateItem],
  )

  const handleOpenActor = useCallback(
    (item: NotificationListItem) => {
      if (!item.actorUserId) return
      if (!item.read) markItemRead(item.id)
      navigatePath(item, buildCreatorPath(item.actorUserId))
    },
    [markItemRead, navigatePath],
  )

  const handleOpenNpc = useCallback(
    (item: NotificationListItem) => {
      if (!item.npcId) return
      if (!item.read) markItemRead(item.id)
      useCharacterPreviewStore.getState().setCharacterPreview({
        id: item.npcId,
        name: item.npcName || '',
        avatar: item.npcAvatar || '',
      })
      navigatePath(
        item,
        `/character/${buildCharacterSlug(item.npcName || 'character', item.npcId)}`,
      )
    },
    [markItemRead, navigatePath],
  )

  useEffect(() => {
    setTab(getNotificationTab(tabParam))
  }, [tabParam])

  const handleTabChange = useCallback(
    (nextTab: NotificationTab) => {
      setTab(nextTab)
      if (navigationMode !== 'page') return

      const nextSearchParams = new URLSearchParams(searchParams.toString())
      nextSearchParams.set('tab', getNotificationTabParam(nextTab))
      navigateReplace(router, `/notification?${nextSearchParams.toString()}`)
    },
    [navigationMode, router, searchParams],
  )

  useEffect(() => {
    const trigger = loadMoreRef.current
    if (!trigger || !hasMore || loading) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void loadMore()
      },
      { rootMargin: observerRootMargin },
    )
    observer.observe(trigger)
    return () => observer.disconnect()
  }, [hasMore, loadMore, loading, observerRootMargin])

  useEffect(() => {
    const timer = window.setInterval(() => {
      void poll().catch((error) => console.error('Failed to poll notifications:', error))
    }, NOTIFICATION_REFRESH_INTERVAL)

    return () => window.clearInterval(timer)
  }, [poll])

  return {
    tab,
    setTab: handleTabChange,
    unread,
    items,
    loading,
    loadMoreRef,
    openItem: handleOpenItem,
    openActor: handleOpenActor,
    openNpc: handleOpenNpc,
    readAll,
    refresh,
  }
}
